import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { WhereFilter } from "@types_def/common/filter.types";
import type { DeliveryStatus } from "@types_def/models/delivery.types";
import {
  cancelDeliveriesTableQueries,
  deliveriesTableQueryRoot,
} from "@/features/delivery/deliveries-table-cache";
import { useFindDeliveryByIdQuery } from "@/libs/generated/hooks";
import type { FindAllDeliveryQuery, FindDeliveryByIdQuery } from "@/libs/generated/operations";

type DeliveryListRecord = FindAllDeliveryQuery["findAllDelivery"]["records"][number];
type GenericDeliveryTableData = {
  records: DeliveryListRecord[];
  total: number;
};

type DeliveryCacheSnapshot = {
  deliveryId: number;
  detail: FindDeliveryByIdQuery | undefined;
  lists: Array<[QueryKey, FindAllDeliveryQuery | undefined]>;
  genericTables: Array<[QueryKey, GenericDeliveryTableData | undefined]>;
};

type DeliveryCachePatch = Partial<DeliveryListRecord>;

const genericDeliveryTableRoot = ["Delivery"] as const;

export function getDeliveryDetailQueryKey(id: number) {
  return useFindDeliveryByIdQuery.getKey({ GetByIdInput: { id } });
}

export function getDeliveryListQueryKey() {
  return ["findAllDelivery"] as const;
}

export async function cancelDeliveryCacheUpdates(queryClient: QueryClient, id: number) {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: getDeliveryDetailQueryKey(id), exact: true }),
    queryClient.cancelQueries({ queryKey: getDeliveryListQueryKey() }),
    queryClient.cancelQueries({ queryKey: genericDeliveryTableRoot }),
    cancelDeliveriesTableQueries(queryClient),
  ]);
}

export function snapshotDeliveryCache(queryClient: QueryClient, id: number): DeliveryCacheSnapshot {
  return {
    deliveryId: id,
    detail: queryClient.getQueryData<FindDeliveryByIdQuery>(getDeliveryDetailQueryKey(id)),
    lists: queryClient.getQueriesData<FindAllDeliveryQuery>({
      queryKey: getDeliveryListQueryKey(),
    }),
    genericTables: queryClient.getQueriesData<GenericDeliveryTableData>({
      queryKey: genericDeliveryTableRoot,
    }),
  };
}

export function restoreDeliveryCache(queryClient: QueryClient, snapshot: DeliveryCacheSnapshot) {
  if (snapshot.detail) {
    queryClient.setQueryData(getDeliveryDetailQueryKey(snapshot.deliveryId), snapshot.detail);
  } else {
    queryClient.removeQueries({
      queryKey: getDeliveryDetailQueryKey(snapshot.deliveryId),
      exact: true,
    });
  }

  snapshot.lists.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });

  snapshot.genericTables.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
}

export function patchDeliveryCache(
  queryClient: QueryClient,
  id: number,
  patch: DeliveryCachePatch
) {
  queryClient.setQueryData<FindDeliveryByIdQuery>(getDeliveryDetailQueryKey(id), (previous) => {
    if (!previous) {
      return previous;
    }

    return {
      ...previous,
      findDeliveryById: {
        ...previous.findDeliveryById,
        ...patch,
      },
    };
  });

  queryClient.setQueriesData<FindAllDeliveryQuery>(
    { queryKey: getDeliveryListQueryKey() },
    (previous) => {
      if (!previous) {
        return previous;
      }

      return {
        ...previous,
        findAllDelivery: {
          ...previous.findAllDelivery,
          records: previous.findAllDelivery.records.map((record) =>
            record.id === id
              ? {
                  ...record,
                  ...patch,
                }
              : record
          ),
        },
      };
    }
  );

  for (const [queryKey, data] of queryClient.getQueriesData<GenericDeliveryTableData>({
    queryKey: genericDeliveryTableRoot,
  })) {
    if (data == null) {
      continue;
    }

    const nextRecords = data.records.flatMap((record) => {
      if (record.id !== id) {
        return [record];
      }

      const updatedRecord = { ...record, ...patch };
      return matchesGenericDeliveryTableFilter(queryKey, updatedRecord) ? [updatedRecord] : [];
    });

    if (
      nextRecords.length === data.records.length &&
      nextRecords.every((record, index) => record === data.records[index])
    ) {
      continue;
    }

    queryClient.setQueryData<GenericDeliveryTableData>(queryKey, {
      ...data,
      total: Math.max(0, data.total + (nextRecords.length - data.records.length)),
      records: nextRecords,
    });
  }
}

export function removeDeliveryFromListsCache(queryClient: QueryClient, id: number) {
  queryClient.setQueriesData<FindAllDeliveryQuery>(
    { queryKey: getDeliveryListQueryKey() },
    (previous) => {
      if (!previous) {
        return previous;
      }

      const records = previous.findAllDelivery.records.filter((record) => record.id !== id);
      if (records.length === previous.findAllDelivery.records.length) {
        return previous;
      }

      return {
        ...previous,
        findAllDelivery: {
          ...previous.findAllDelivery,
          total: Math.max(0, previous.findAllDelivery.total - 1),
          records,
        },
      };
    }
  );

  queryClient.setQueriesData<GenericDeliveryTableData>(
    { queryKey: genericDeliveryTableRoot },
    (previous) => {
      if (!previous) {
        return previous;
      }

      const records = previous.records.filter((record) => record.id !== id);
      if (records.length === previous.records.length) {
        return previous;
      }

      return {
        ...previous,
        total: Math.max(0, previous.total - 1),
        records,
      };
    }
  );
}

export async function invalidateDeliveryListCaches(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: getDeliveryListQueryKey() }),
    queryClient.invalidateQueries({ queryKey: genericDeliveryTableRoot }),
    queryClient.invalidateQueries({ queryKey: deliveriesTableQueryRoot }),
  ]);
}

export async function invalidateDeliveryCache(queryClient: QueryClient, id: number) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: getDeliveryDetailQueryKey(id), exact: true }),
    invalidateDeliveryListCaches(queryClient),
  ]);
}

/** Bulk-friendly: one pass over shared list caches plus each detail key (deduped). */
export async function invalidateCachesForDeliveryIds(queryClient: QueryClient, ids: number[]) {
  const uniqueIds = [...new Set(ids)];
  if (uniqueIds.length === 0) {
    return;
  }
  await Promise.all([
    ...uniqueIds.map((id) =>
      queryClient.invalidateQueries({ queryKey: getDeliveryDetailQueryKey(id), exact: true })
    ),
    invalidateDeliveryListCaches(queryClient),
  ]);
}

export async function invalidateDeliveryDetailCache(queryClient: QueryClient, id: number) {
  await queryClient.invalidateQueries({ queryKey: getDeliveryDetailQueryKey(id), exact: true });
}

export function removeDeliveryDetailCache(queryClient: QueryClient, id: number) {
  queryClient.removeQueries({ queryKey: getDeliveryDetailQueryKey(id), exact: true });
}

function matchesGenericDeliveryTableFilter(queryKey: QueryKey, record: DeliveryListRecord) {
  const filter = getGenericDeliveryTableFilter(queryKey);
  if (filter == null) {
    return true;
  }

  if (!matchesStatusFilter(filter.status, record.status)) {
    return false;
  }

  if (typeof filter.id === "number" && filter.id !== record.id) {
    return false;
  }

  return true;
}

function getGenericDeliveryTableFilter(queryKey: QueryKey) {
  const filter = queryKey[2];
  return isRecord(filter) ? (filter as WhereFilter<DeliveryListRecord>) : undefined;
}

function matchesStatusFilter(
  statusFilter: WhereFilter<DeliveryListRecord>["status"] | undefined,
  status: DeliveryStatus
) {
  if (statusFilter == null) {
    return true;
  }

  if (typeof statusFilter === "string") {
    return statusFilter === status;
  }

  if (isRecord(statusFilter) && Array.isArray(statusFilter.in)) {
    return statusFilter.in.includes(status);
  }

  return true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
